import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input } from '@angular/core';
import {Router} from '@angular/router';
import {ReleaseSummarySearchCriteria} from './release-summary-search-criteria';
import {ReleaseSummary} from './release-summary';
import {PersonLogin} from '../../person/person-login';
import {PersonLoginService} from '../../services/person-login-service';
import {ReleaseSummaryService} from '../../services/release-summary-service';
import {StringUtils} from '../../utils/string-utils';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'release-summary',
    templateUrl: './release-summary.component.html',
    styleUrls: ['./release-summary.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, FormsModule, MatProgressSpinner, CurrencyPipe, DatePipe]
})
export class ReleaseSummaryComponent implements OnInit {
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });
  private readonly personLoginService = inject(PersonLoginService);
  private readonly releaseSummaryService = inject(ReleaseSummaryService);
  private readonly router = inject(Router);


  private personLogin!: PersonLogin;
  readonly releaseResults = signal<ReleaseSummary[]>(undefined!);
  readonly maxPage = signal<number>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  public criteria!: ReleaseSummarySearchCriteria;
  readonly searchComplete = signal<boolean>(undefined!);
  readonly selectedRowId = signal<number>(-1);

  public previousPage(): void {
    this.criteria.pageNumber -= 1;
    this.getResultPage();
  }

  public nextPage(): void {
    this.criteria.pageNumber += 1;
    this.getResultPage();
  }

  public search(): void {
    this.criteria.pageNumber = 1;
    this.criteria.sortColumn = 'ENTITY_ID';
    this.criteria.sortDirection = 'ASCENDING';
    this.maxPage.set(this.criteria.pageNumber);
    this.getResultPage();
  }

  public sortResults(columnName: string): void {
    if (this.criteria.sortColumn == columnName) {
      this.criteria.sortDirection = this.isDescending() ? "ASCENDING" : "DESCENDING";
    } else {
      this.criteria.sortColumn = columnName;
      this.criteria.sortDirection = "ASCENDING";
    }
    this.getResultPage();
  }

  isDescending(): boolean {
    return this.criteria.sortDirection == 'DESCENDING';
  }

  public getResultPage(): void {
    if (this.criteria.pageNumber <= this.maxPage()) {
      this.clearStatus();
      this.releaseSummaryService.getReleases(this.criteria).subscribe(results => {
        this.releaseResults.set(results);
        this.isBusy.set(false);
        this.searchComplete.set(true);
        if (results.length > 0) {
          this.maxPage.set(Math.ceil(results[0].recordCount / 20));
        }
      }, (error: HttpErrorResponse) => {
        this.errorMessage.set(StringUtils.getErrorMessage(error));
      });
    }
  }

  private clearStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.searchComplete.set(false);
    this.releaseResults.set(undefined!);
    this.selectedRowId.set(-1);
  }

  private handleError(error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.criteria = new ReleaseSummarySearchCriteria();
      this.personLogin = this.personLoginService.getCachedLogin()!;
      if (!this.personLogin) {
        this.router.navigate(['main']);
      } else {
        this.criteria.entityId = this.entityId();
        this.criteria.entityRevision = +this.entityRevision();
        this.criteria.sortColumn = 'ENTITY_ID';
        this.criteria.sortDirection = 'ASCENDING';
        this.criteria.excludeCancelled = false;
        this.search();
      }
    }
  }

  public goToDetail() {
    this.router.navigate(['orders', this.criteria.entityId, this.criteria.entityRevision]);
  }

  public selectRow(index: number, release: ReleaseSummary): void {
    this.selectedRowId.set(index);
    let url = this.router.createUrlTree(['/orders', release.id.entityId, release.id.entityRevision]);
    window.open(url.toString(), '_blank')
  }

}
